import { Controller } from '@hotwired/stimulus';
import { StreamActions } from '@hotwired/turbo';
import MicroModal from 'micromodal';
import { openModal } from 'banzai/pendant/front/util/modals.js';
import { createFrameListener } from 'banzai/pendant/front/util/turbo.js';

export default class extends Controller {
  static targets = ['modal'];

  connect() {
    // Initialize debounce state
    this.lastModalOperation = 0;
    this.modalOperationDebounceMs = 300; // 300ms minimum delay between operations

    this.modalTargets.forEach((modalTarget) => {
      createFrameListener('turbo:frame-render', modalTarget.id, openModal(modalTarget));

      StreamActions.removeModal = function () {
        const controller = this;
        return function () {
          controller.debouncedModalOperation(() => {
            const id = this.dataset.modalId;
            const modalElement = document.getElementById(id);

            if (modalElement) {
              // Clean up Alpine components before closing
              controller.cleanupAlpineComponents(modalElement);
            }

            MicroModal.close(id);
            // It wasn't closing on its own, so I'm forcing it to close and then removing it
            // from the DOM.
            modalElement.remove();
          });
        };
      }.bind(this)();
    });

    MicroModal.init({
      disableScroll: true,
      onClose: (modal) => {
        this.debouncedModalOperation(() => {
          this.cleanupAlpineComponents(modal);
        });
      },
    });

    // Also watch for when modals are removed from the DOM
    this.setupModalCleanupObserver();
  }

  setupModalCleanupObserver() {
    // Watch for when modals are removed from the DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.removedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains('modal')) {
            // Modal was removed, clean up any remaining Alpine components
            this.cleanupAlpineComponents(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    this.observer = observer;
  }

  cleanupAlpineComponents(modal) {
    if (!modal) return;

    try {
      // Find and destroy Alpine.js components within the modal
      const alpineElements = modal.querySelectorAll('[x-data]');
      alpineElements.forEach((element) => {
        if (element._x_dataStack && element._x_dataStack[0]) {
          // Call destroy method if it exists
          const component = element._x_dataStack[0];
          if (typeof component.destroy === 'function') {
            try {
              component.destroy();
            } catch (error) {
              console.warn('Error destroying Alpine component:', error);
            }
          }
        }
      });

      // Also clear any turbo frames to ensure clean state
      const turboFrames = modal.querySelectorAll('turbo-frame');
      turboFrames.forEach((frame) => {
        if (frame.src) {
          frame.src = '';
        }
        frame.innerHTML = '';
      });
    } catch (error) {
      console.warn('Error during Alpine component cleanup:', error);
    }
  }

  close(event) {
    this.debouncedModalOperation(() => {
      MicroModal.close(event.currentTarget.dataset.modalId);
    });
  }

  debouncedModalOperation(operation) {
    const now = Date.now();
    const timeSinceLastOperation = now - this.lastModalOperation;

    if (timeSinceLastOperation < this.modalOperationDebounceMs) {
      const delay = this.modalOperationDebounceMs - timeSinceLastOperation;
      setTimeout(() => {
        operation();
        this.lastModalOperation = Date.now();
      }, delay);
    } else {
      operation();
      this.lastModalOperation = now;
    }
  }

  isModalOperationInProgress() {
    const now = Date.now();
    const timeSinceLastOperation = now - this.lastModalOperation;
    return timeSinceLastOperation < this.modalOperationDebounceMs;
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}
